import React from 'react';
import { Link } from 'react-router-dom';
import { Calculator, DollarSign, Receipt, TrendingUp, ArrowRight } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import SeoHead from '@/components/shared/SeoHead';
import PageHero from '@/components/shared/PageHero';
import AdSlot from '@/components/shared/AdSlot';
import AdFrame from '@/components/shared/AdFrame';
import { RectangleAd, InContentAd } from '@/components/shared/AdSense';

const Calculators = () => {
  const calculators = [
    {
      title: "Salary Calculator",
      description: "Convert between hourly and annual wages, find comparable jobs, and see your wage percentile",
      icon: Calculator,
      href: "/calculators/salary",
      features: ["Hourly to Annual Conversion", "Comparable Jobs", "Wage Percentiles", "Daily & Weekly Pay"],
      popular: true
    },
    {
      title: "Tax Calculator", 
      description: "Calculate federal and state taxes, see take-home pay, and compare across states",
      icon: Receipt,
      href: "/calculators/tax",
      features: ["Federal Tax Calculation", "State Tax Breakdown", "Take-Home Pay", "Effective Tax Rates"],
      popular: true
    },
    {
      title: "Income Percentile Calculator",
      description: "See what percent of Americans earn your salary, and which jobs actually pay it",
      icon: TrendingUp,
      href: "/income-percentile",
      features: ["US Earner Percentile", "Full-Time Worker Cut", "Percentile Reference Table", "Occupations at That Pay"],
      popular: false
    }
  ];

  const popularCalculations = [
    { label: "$50k Salary", href: "/calculators/salary/50k" },
    { label: "$25/hour", href: "/calculators/salary/25-per-hour" },
    { label: "$75k Salary", href: "/calculators/salary/75k" },
    { label: "$30/hour", href: "/calculators/salary/30-per-hour" }
  ];

  return (
    <>
      <SeoHead
        title="Salary & Tax Calculators - Free Financial Planning Tools | Wage Atlas"
        description="Free salary and tax calculators to help with financial planning. Convert wages, calculate taxes, and compare salaries across different states and occupations."
        
        occupation="Financial Calculators"
        canonical="/calculators/"
      />
      
      <div className="min-h-screen bg-background">
        <PageHero
          title="Financial Calculators"
          subtitle="Free, accurate calculators to help you make informed financial decisions. Calculate salaries, taxes, and plan your financial future with real-time data."
        >
          <AdFrame className="flex justify-center">
            <AdSlot slot="leaderboard" className="max-w-4xl" />
          </AdFrame>
        </PageHero>

        {/* Calculator Cards */}
        <section className="py-8 px-4">
          <div className="max-w-6xl mx-auto">
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
              {/* Calculator Cards */}
              <div className="lg:col-span-2 grid md:grid-cols-2 gap-8">
              {calculators.map((calc) => {
                const IconComponent = calc.icon;
                return (
                  <Card key={calc.title} className="relative overflow-hidden border-2 hover:border-primary/50 transition-all duration-300 hover:shadow-lg">
                    {calc.popular && (
                      <div className="absolute top-4 right-4">
                        <span className="bg-primary text-primary-foreground px-2 py-1 rounded-full text-xs font-medium">
                          Most Popular
                        </span>
                      </div>
                    )}
                    <CardHeader className="pb-4">
                      <div className="flex items-center gap-3 mb-2">
                        <div className="p-2 rounded-lg bg-primary/10">
                          <IconComponent className="h-6 w-6 text-primary" />
                        </div>
                        <CardTitle className="text-xl">{calc.title}</CardTitle>
                      </div>
                      <CardDescription className="text-base">
                        {calc.description}
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="space-y-2">
                        <h4 className="font-medium text-sm text-muted-foreground">Key Features:</h4>
                        <ul className="space-y-1">
                          {calc.features.map((feature, index) => (
                            <li key={index} className="flex items-center gap-2 text-sm">
                              <TrendingUp className="h-3 w-3 text-green-500" />
                              {feature}
                            </li>
                          ))}
                        </ul>
                      </div>
                      <Button asChild className="w-full">
                        <Link to={calc.href} className="flex items-center gap-2">
                          Open Calculator
                          <ArrowRight className="h-4 w-4" />
                        </Link>
                      </Button>
                    </CardContent>
                  </Card>
                );
              })}
              </div>
              
              {/* Sidebar Ad */}
              <div className="hidden lg:block">
                <RectangleAd className="sticky top-4" />
              </div>
            </div>

            {/* In-Content Ad */}
            <AdFrame className="mb-12">
              <InContentAd />
            </AdFrame>

            {/* Popular Calculations */}
            <Card className="bg-muted/50">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <DollarSign className="h-5 w-5" />
                  Popular Calculations
                </CardTitle>
                <CardDescription>
                  Quick access to commonly searched salary calculations
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {popularCalculations.map((calc) => (
                    <Button key={calc.label} variant="outline" asChild className="h-auto py-3">
                      <Link to={calc.href} className="flex flex-col items-center gap-1">
                        <span className="font-medium">{calc.label}</span>
                      </Link>
                    </Button>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Features Section */}
        <section className="py-16 px-4 bg-muted/30">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl font-bold mb-8">Why Use Our Calculators?</h2>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="space-y-3">
                <div className="p-3 rounded-full bg-green-100 dark:bg-green-900/20 w-fit mx-auto">
                  <TrendingUp className="h-6 w-6 text-green-600" />
                </div>
                <h3 className="font-semibold">Real-Time Data</h3>
                <p className="text-sm text-muted-foreground">
                  Based on current market data and official government statistics
                </p>
              </div>
              <div className="space-y-3">
                <div className="p-3 rounded-full bg-blue-100 dark:bg-blue-900/20 w-fit mx-auto">
                  <Calculator className="h-6 w-6 text-blue-600" />
                </div>
                <h3 className="font-semibold">Always Free</h3>
                <p className="text-sm text-muted-foreground">
                  No hidden fees, no registration required, completely free to use
                </p>
              </div>
              <div className="space-y-3">
                <div className="p-3 rounded-full bg-purple-100 dark:bg-purple-900/20 w-fit mx-auto">
                  <Receipt className="h-6 w-6 text-purple-600" />
                </div>
                <h3 className="font-semibold">Comprehensive</h3>
                <p className="text-sm text-muted-foreground">
                  Multiple calculation types with detailed breakdowns and comparisons
                </p>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default Calculators;